import React, { useState } from 'react';

/**
 * Explorer parameter controls — SimulationSettingsPanel style.
 * Collapsible panel with call/put toggle, exit % slider, risk-free rate, IV crush.
 * Tickers shown as read-only pills from the main Analysis Input.
 */
function ExplorerControls({ tickers, onGenerate, loading }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [optionType, setOptionType] = useState('call');
  const [exitPct, setExitPct] = useState(50);
  const [riskFreeRate, setRiskFreeRate] = useState(4.5);
  const [ivCrushRate, setIvCrushRate] = useState(15);

  const handleGenerate = () => {
    onGenerate({
      tickers,
      option_type: optionType,
      risk_free_rate: riskFreeRate / 100,
      iv_crush_rate: ivCrushRate / 100,
      exit_pct: exitPct,
    });
  };

  return (
    <div className="bg-gray-800 rounded-lg">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-200">Explorer Settings</h3>
          <span className="text-xs text-gray-400">
            {optionType === 'call' ? 'Calls' : 'Puts'} · Exit {exitPct}% · Crush {ivCrushRate}%
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleGenerate();
              }}
              disabled={loading}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
          )}
          <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            ›
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Tickers (read-only pills) */}
          <div>
            <label className="text-xs font-medium text-gray-300 mb-2 block">Tickers</label>
            <div className="flex flex-wrap gap-2">
              {tickers.map(ticker => (
                <span
                  key={ticker}
                  className="px-3 py-1 text-sm bg-gray-700 text-white rounded-full"
                >
                  {ticker}
                </span>
              ))}
            </div>
          </div>

          {/* Call / Put Toggle */}
          <div>
            <label className="text-xs font-medium text-gray-300 mb-2 block">Option Type</label>
            <div className="flex gap-3">
              <button
                onClick={() => setOptionType('call')}
                className={`px-5 py-2 text-sm rounded-md font-medium transition-colors ${
                  optionType === 'call'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Calls (Bullish)
              </button>
              <button
                onClick={() => setOptionType('put')}
                className={`px-5 py-2 text-sm rounded-md font-medium transition-colors ${
                  optionType === 'put'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Puts (Bearish)
              </button>
            </div>
          </div>

          {/* Exit % Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-300">Exit at % of DTE</label>
              <span className="text-sm font-bold text-blue-500">{exitPct}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="95"
              value={exitPct}
              onChange={(e) => setExitPct(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #218aff 0%, #218aff ${
                  ((exitPct - 10) / 85) * 100
                }%, #374151 ${((exitPct - 10) / 85) * 100}%, #374151 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>10%</span>
              <span>50%</span>
              <span>95%</span>
            </div>
            <p className="text-xs text-gray-500">
              e.g., 50% means a 90 DTE contract is modeled as selling at day 45
            </p>
          </div>

          {/* Risk-Free Rate + IV Crush (side by side) */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-300 mb-1 block">Risk-Free Rate (%)</label>
              <input
                type="number"
                value={riskFreeRate}
                onChange={(e) => setRiskFreeRate(Number(e.target.value))}
                min={0}
                max={20}
                step={0.1}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-300 mb-1 block">IV Crush Rate (%)</label>
              <input
                type="number"
                value={ivCrushRate}
                onChange={(e) => setIvCrushRate(Number(e.target.value))}
                min={0}
                max={100}
                step={1}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                How much IV drops per 100% move
              </p>
            </div>
          </div>

          {/* Generate Button */}
          <div className="pt-2">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Generating Contracts...
                </div>
              ) : (
                'Generate Contracts'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExplorerControls;
